import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Trash2, Copy, Search, RefreshCw } from "lucide-react";
import { SkuImage, useShowPhoto } from "@/components/SkuImage";
import { useUiPrefs } from "@/lib/uiPrefs";
import { DensityToggle, FilterCard, tableDensity } from "@/components/ops-ui";

export default function AdjustLog() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [showPhoto, ShowPhotoToggle] = useShowPhoto("adjust-log");
  const { density, setDensity, filtersOpen, setFiltersOpen } = useUiPrefs("adjust-log");

  const load = () => api.get("/skus/stock-adjustments").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const del = async (row) => {
    if (!window.confirm(`Delete this adjustment? Stock for ${row.sku} will be restored by ${-row.delta} pcs.`)) return;
    try {
      await api.delete(`/skus/stock-adjustments/${row.id}`);
      toast.success("Adjustment deleted · stock restored");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const dup = async (row) => {
    if (!window.confirm(`Duplicate this adjustment? Stock for ${row.sku} will be deducted by another ${-row.delta} pcs.`)) return;
    try {
      await api.post(`/skus/stock-adjustments/${row.id}/duplicate`);
      toast.success("Adjustment duplicated · stock re-deducted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Duplicate failed"); }
  };

  const filtered = rows.filter(r => {
    if (!q) return true;
    const t = q.toLowerCase();
    return (r.sku || "").toLowerCase().includes(t)
      || (r.sku_name || "").toLowerCase().includes(t)
      || (r.note || "").toLowerCase().includes(t);
  });

  const totalDelta = filtered.reduce((s, r) => s + (r.delta || 0), 0);
  const totalValue = filtered.reduce((s, r) => s + (r.value_rm || 0), 0);

  return (
    <div data-testid="adjust-log-page">
      <header className="mb-4 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="caption">Audit trail</div>
          <h1 className="font-heading text-3xl font-extrabold mt-1">Stock Adjustment Log</h1>
          <p className="text-slate-500 mt-1 text-sm">Every weekly deduct upload is logged here. Delete a row to restore its stock, or duplicate a row to re-apply the same deduction.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <DensityToggle density={density} setDensity={setDensity} />
          {ShowPhotoToggle}
          <Button variant="outline" onClick={load} data-testid="al-refresh" className="h-9"><RefreshCw className="w-4 h-4 mr-1.5"/>Refresh</Button>
        </div>
      </header>

      <div className="mb-4">
        <FilterCard open={filtersOpen} setOpen={setFiltersOpen} activeCount={q ? 1 : 0}>
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <label className="text-xs text-slate-500 block mb-1">Search (SKU / name / note)</label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input placeholder="Search SKU, note…" value={q} onChange={e=>setQ(e.target.value)} className="pl-9 h-9" data-testid="al-search" />
              </div>
            </div>
          </div>
        </FilterCard>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div className="rounded border border-slate-200 bg-white p-3">
          <div className="caption text-slate-500">Adjustments shown</div>
          <div className="font-mono font-bold text-2xl mt-1">{filtered.length}</div>
        </div>
        <div className="rounded border border-slate-200 bg-white p-3">
          <div className="caption text-slate-500">Net stock delta</div>
          <div className={`font-mono font-bold text-2xl mt-1 ${totalDelta < 0 ? "text-rose-600" : "text-emerald-600"}`}>{totalDelta}</div>
        </div>
        <div className="rounded border border-slate-200 bg-white p-3">
          <div className="caption text-slate-500">Est. value RM</div>
          <div className="font-mono font-bold text-2xl mt-1">RM {totalValue.toFixed(2)}</div>
        </div>
      </div>

      {/* desktop / tablet table */}
      <div className="hidden md:block rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className={`excel-table w-full text-xs ${tableDensity(density)}`} data-testid="al-table">
          <thead><tr>
            <th>When</th>
            {showPhoto && <th className="w-16">Image</th>}
            <th>SKU</th><th>Name</th>
            <th className="text-right">Delta</th>
            <th className="text-right">Avg RM (at time)</th>
            <th className="text-right">Value RM</th>
            <th>Note</th><th>By</th>
            <th className="text-right w-32">Actions</th>
          </tr></thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.id} data-testid={`al-row-${r.id}`}>
                <td className="font-mono text-slate-500 text-[11px]">{(r.at || "").slice(0, 19).replace("T", " ")}</td>
                {showPhoto && <td><SkuImage url={r.image_url} alt={r.sku} size={50} /></td>}
                <td className="font-mono font-bold">{r.sku}</td>
                <td className="truncate max-w-[280px]">{r.sku_name || "—"}</td>
                <td className={`text-right font-mono ${r.delta < 0 ? "text-rose-600" : "text-emerald-600"}`}>{r.delta}</td>
                <td className="text-right font-mono">{r.avg_rm_inc_freight_at_time?.toFixed?.(4) ?? "—"}</td>
                <td className="text-right font-mono">RM {(r.value_rm ?? 0).toFixed(2)}</td>
                <td className="text-slate-600">{r.note || "—"}</td>
                <td className="font-mono text-[11px] text-slate-500">{r.by || "—"}</td>
                <td className="text-right">
                  <Button variant="ghost" size="sm" onClick={()=>dup(r)} data-testid={`al-dup-${r.id}`} title="Duplicate — deduct again">
                    <Copy className="w-4 h-4 text-sky-600" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={()=>del(r)} data-testid={`al-del-${r.id}`} className="text-rose-600 hover:bg-rose-50" title="Delete — restore stock">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={showPhoto ? 10 : 9} className="text-center py-8 text-slate-400">No adjustments yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* mobile cards */}
      <div className="md:hidden space-y-2">
        {filtered.length === 0 && <div className="text-center py-8 text-slate-400">No adjustments yet.</div>}
        {filtered.map(r => (
          <div key={r.id} data-testid={`al-card-${r.id}`} className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                {showPhoto && <SkuImage url={r.image_url} alt={r.sku} size={36} />}
                <span className="font-mono font-bold truncate">{r.sku}</span>
              </div>
              <span className={`font-mono font-semibold ${r.delta < 0 ? "text-rose-600" : "text-emerald-600"}`}>{r.delta}</span>
            </div>
            <div className="text-xs text-slate-500 mt-0.5 truncate">{r.sku_name || "—"}</div>
            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-slate-500">
              <span>RM {(r.value_rm ?? 0).toFixed(2)}</span>
              <span className="font-mono">{(r.at || "").slice(0, 19).replace("T", " ")}</span>
              {r.note && <span className="text-slate-600">{r.note}</span>}
            </div>
            <div className="mt-2 flex gap-2">
              <Button variant="outline" size="sm" onClick={()=>dup(r)} data-testid={`al-card-dup-${r.id}`} className="h-8 text-xs"><Copy className="w-3.5 h-3.5 mr-1 text-sky-600" />Duplicate</Button>
              <Button variant="outline" size="sm" onClick={()=>del(r)} data-testid={`al-card-del-${r.id}`} className="h-8 text-xs text-rose-600"><Trash2 className="w-3.5 h-3.5 mr-1" />Delete</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
